import React, { createContext, useContext, useEffect, useState } from 'react'
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'

const ThemeContext = createContext()

export const useTheme = () => {
	const context = useContext(ThemeContext)
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider')
	}

	return context
}

const lightTheme = createTheme({
	palette: {
		mode: 'light',
		primary: {
			main: '#1976d2',
		},
		secondary: {
			main: '#dc004e',
		},
		background: {
			default: '#ffffff',
			paper: '#ffffff',
		},
		text: {
			primary: '#000000',
			secondary: '#666666',
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					backgroundColor: '#ffffff',
					color: '#000000',
				},
			},
		},
	},
})

const darkTheme = createTheme({
	palette: {
		mode: 'dark',
		primary: {
			main: '#1565c0',
			contrastText: '#ffffff',
		},
		secondary: {
			main: '#e91e63',
			contrastText: '#ffffff',
		},
		background: {
			default: '#2b2b2b',
			paper: '#363636',
		},
		text: {
			primary: '#ffffff',
			secondary: '#aaaaaa',
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					backgroundColor: '#2b2b2b',
					color: '#ffffff',
				},
			},
		},
	},
})

export const ThemeProvider = ({ children }) => {
	const [isDarkMode, setIsDarkMode] = useState(() => {
		const saved = localStorage.getItem('darkMode')

		return saved ? JSON.parse(saved) : true
	})

	// Keep Bootstrap/reactstrap components (navbar, containers, ...) in sync with the MUI theme
	useEffect(() => {
		document.documentElement.setAttribute('data-bs-theme', isDarkMode ? 'dark' : 'light')
	}, [isDarkMode])

	const toggleTheme = () => {
		setIsDarkMode((prev) => {
			const newValue = !prev
			localStorage.setItem('darkMode', JSON.stringify(newValue))

			return newValue
		})
	}

	const currentTheme = isDarkMode ? darkTheme : lightTheme

	return (
		<ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
			<MuiThemeProvider theme={currentTheme}>
				<CssBaseline />
				{children}
			</MuiThemeProvider>
		</ThemeContext.Provider>
	)
}
